<script setup lang="ts">
import { ref, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components'

const props = defineProps<{
    modelValue: boolean
    isSubmitting?: boolean
}>()

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'confirm': [reason: string]
}>()

const selectedReason = ref('Not interested')
const customReason = ref('')

const commonReasons = [
    'Not interested',
    'Budget constraints',
    'Out of service area',
    'Commercial roof outside specialty',
    'Duplicate record',
    'Invalid contact information',
    'Competitor chosen',
    'Other',
]

watch(() => props.modelValue, (isOpen) => {
    if (isOpen) {
        selectedReason.value = 'Not interested'
        customReason.value = ''
    }
})

function handleConfirm() {
    const finalReason = selectedReason.value === 'Other' && customReason.value.trim()
        ? customReason.value.trim()
        : selectedReason.value
    emit('confirm', finalReason)
}
</script>

<template>
    <BaseModal
        :model-value="modelValue"
        title="Disqualify Lead"
        size="md"
        @update:model-value="(v) => emit('update:modelValue', v)"
    >
        <div class="disqualify-modal">
            <p class="disqualify-modal__desc">
                Please specify the primary reason for marking this lead as unqualified.
            </p>

            <div class="form-group">
                <label class="form-label" for="disqualify-reason-select">Reason</label>
                <select
                    id="disqualify-reason-select"
                    v-model="selectedReason"
                    class="form-input form-select"
                >
                    <option v-for="reason in commonReasons" :key="reason" :value="reason">
                        {{ reason }}
                    </option>
                </select>
            </div>

            <div v-if="selectedReason === 'Other'" class="form-group">
                <label class="form-label" for="disqualify-details">Additional Details</label>
                <textarea
                    id="disqualify-details"
                    v-model="customReason"
                    rows="3"
                    class="form-input form-textarea"
                    placeholder="Provide specific notes regarding disqualification..."
                />
            </div>
        </div>

        <template #footer>
            <div class="modal-actions">
                <button
                    type="button"
                    class="action-link"
                    :disabled="isSubmitting"
                    @click="emit('update:modelValue', false)"
                >
                    Cancel
                </button>
                <BaseButton
                    variant="danger"
                    :loading="isSubmitting"
                    @click="handleConfirm"
                >
                    Disqualify Lead
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<style scoped>
.disqualify-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.disqualify-modal__desc {
    font-size: var(--text-sm);
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.form-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
}

.form-input {
    padding: 0.5rem 0.625rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.form-select {
    cursor: pointer;
}

.form-textarea {
    resize: vertical;
    min-height: 60px;
}

.form-input:focus {
    outline: none;
    border-color: var(--color-error);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
}

.modal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    width: 100%;
}

.action-link {
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    cursor: pointer;
    padding: 0.375rem 0.75rem;
    transition: color 0.15s;
}

.action-link:hover {
    color: var(--text-primary);
}
</style>
